import { useAuth0 } from '@auth0/auth0-react';
import { useGetPlatformFeeSettingQuery } from '../../api/userApi2';

export const PRICING_CONSTANTS = {
  BASE_PRICE_USD: 25,
  PLATFORM_FEE_PERCENTAGE: 0.1,
  MIN_PRICE_USD: 25,
  MAX_PRICE_USD: 3000,
} as const;

export interface PricingResult {
  basePrice: number;
  platformFee: number;
  totalPayable: number;
  pricePerLb: number;
}

export interface DynamicPricingConstants {
  percentage: number;
}

export interface PriceLimitsUSD {
  minPriceUSD: number;
  maxPriceUSD: number;
}

export const usePriceLimitsUSD = (): PriceLimitsUSD => {
  const { isAuthenticated } = useAuth0();
  const { data } = useGetPlatformFeeSettingQuery({ enabled: isAuthenticated });
  const minPriceUSD = parseFloat(String(data?.first_premium_price ?? PRICING_CONSTANTS.MIN_PRICE_USD));
  const maxPriceUSD = parseFloat(String(data?.price_upper_limit ?? PRICING_CONSTANTS.MAX_PRICE_USD));
  return {
    minPriceUSD: isNaN(minPriceUSD) ? PRICING_CONSTANTS.MIN_PRICE_USD : minPriceUSD,
    maxPriceUSD: isNaN(maxPriceUSD) ? PRICING_CONSTANTS.MAX_PRICE_USD : maxPriceUSD,
  };
};

export const usePricingConstants = (): DynamicPricingConstants => {
  const { isAuthenticated } = useAuth0();
  const { data } = useGetPlatformFeeSettingQuery({ enabled: isAuthenticated });
  const percentage = parseFloat(String(data?.platform_fee_percentage ?? (PRICING_CONSTANTS.PLATFORM_FEE_PERCENTAGE * 100))) / 100;
  return {
    percentage: isNaN(percentage) ? PRICING_CONSTANTS.PLATFORM_FEE_PERCENTAGE : percentage,
  };
};

export const calculatePricePerLb = (
  travelerPrice: number,
  travelerWeight: number
): number => {
  if (!travelerWeight || travelerWeight <= 0 || !travelerPrice || travelerPrice < 0) {
    return 0;
  }
  return travelerPrice / travelerWeight;
};

export const calculateBasePrice = (
  senderWeight: number,
  pricePerLb: number
): number => {
  if (!senderWeight || senderWeight <= 0) {
    return 0;
  }

  return Math.max(0, senderWeight * pricePerLb);
};

export const calculatePlatformFee = (
  basePrice: number,
  platformFeePercentage: number = PRICING_CONSTANTS.PLATFORM_FEE_PERCENTAGE
): number => {
  if (!basePrice || basePrice < 0) {
    return 0;
  }

  return Math.max(0, basePrice * platformFeePercentage);
};

export const calculateTotalPayable = (
  basePrice: number,
  platformFee: number
): number => {
  return Math.max(0, basePrice + platformFee);
};

export const calculateShippingPrice = (
  senderWeight: number,
  travelerPrice: number,
  travelerWeight: number,
  platformFeePercentage?: number
): PricingResult => {
  const pricePerLb = calculatePricePerLb(travelerPrice, travelerWeight);
  const basePrice = calculateBasePrice(senderWeight, pricePerLb);
  const platformFee = calculatePlatformFee(basePrice, platformFeePercentage);
  const totalPayable = calculateTotalPayable(basePrice, platformFee);

  return {
    basePrice: isNaN(basePrice) || basePrice < 0 ? 0 : basePrice,
    platformFee: isNaN(platformFee) || platformFee < 0 ? 0 : platformFee,
    totalPayable: isNaN(totalPayable) || totalPayable < 0 ? 0 : totalPayable,
    pricePerLb: isNaN(pricePerLb) || pricePerLb < 0 ? 0 : pricePerLb,
  };
};

// Single source of truth for the "How final price/earning is calculated?" breakdown
export interface PricingBreakdown {
  pricePerLb: number;
  basePrice: number;
  platformFee: number;
  finalAmount: number;
}

export const calculatePricingBreakdown = (
  senderWeight: number,
  travelerPrice: number,
  travelerWeight: number,
  feePercentage: number,
  direction: 'sender' | 'traveler'
): PricingBreakdown => {
  const pricePerLb = direction === 'traveler'
    ? calculatePricePerLb(travelerPrice, senderWeight)
    : calculatePricePerLb(travelerPrice, travelerWeight);
  const basePrice = calculateBasePrice(senderWeight, pricePerLb);
  const platformFee = calculatePlatformFee(basePrice, feePercentage / 100);
  const finalAmount = direction === 'traveler' ? basePrice - platformFee : basePrice + platformFee;

  return { pricePerLb, basePrice, platformFee, finalAmount };
};

export const buildPricingFormulaText = (
  senderWeight: number,
  travelerPrice: number,
  travelerWeight: number,
  feePercentage: number,
  direction: 'sender' | 'traveler'
): string => {
  const { pricePerLb, basePrice, platformFee, finalAmount } = calculatePricingBreakdown(
    senderWeight,
    travelerPrice,
    travelerWeight,
    feePercentage,
    direction
  );

  const weight = senderWeight.toFixed(2);
  const travelerWeightStr = travelerWeight.toFixed(2);
  const price = travelerPrice.toFixed(2);
  const pricePerLbStr = pricePerLb.toFixed(2);
  const basePriceStr = basePrice.toFixed(2);
  const feePercentageStr = feePercentage.toFixed(0);
  const platformFeeStr = platformFee.toFixed(2);
  const finalAmountStr = finalAmount.toFixed(2);

  if (direction === 'traveler') {
    return `If the package weighs ${weight} lbs then:\nPackage Weight = ${weight} lbs\nTotal Shipping Price = $${price}\nAverage Price Per Pound = $${price}/${weight} = $${pricePerLbStr}/lb\nPlatform Fee = ${feePercentageStr}% of $${basePriceStr} = $${platformFeeStr}\nFinal Earning = Weight × Avg Price Per pound - Platform fee\nFinal Earning = $${basePriceStr} - $${platformFeeStr} = $${finalAmountStr}`;
  }

  return `Package Weight = ${weight} lbs\nPrice = $${price}\nAverage Price Per Pound = $${price}/${travelerWeightStr} = $${pricePerLbStr}/lb\nPlatform Fee = ${feePercentageStr}% of $${basePriceStr} = $${platformFeeStr}\nFinal Price = Weight × Avg Price Per pound + Platform fee\nFinal Price = $${basePriceStr} + $${platformFeeStr} = $${finalAmountStr}`;
};
